'use client';

import { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './ScrollablePriceText.module.scss';
interface ScrollablePriceTextProps {
  children: ReactNode;
  className?: string;
  title?: string;
}
const ScrollablePriceText: FC<ScrollablePriceTextProps> = ({
  children,
  className,
  title
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLSpanElement>(null);
  const [isScrollable, setIsScrollable] = useState(false);
  const [tooltipPos, setTooltipPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const updateOverflow = useCallback(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;
    const parent = container.parentElement;
    let availableWidth = container.clientWidth;
    if (parent) {
      const parentStyles = getComputedStyle(parent);
      const gap = parseFloat(parentStyles.columnGap || parentStyles.gap || '0') || 0;
      const siblingWidth = Array.from(parent.children).reduce((total, child) => {
        if (child === container) return total;
        return total + child.getBoundingClientRect().width;
      }, 0);
      availableWidth = Math.max(0, parent.clientWidth - siblingWidth - gap);
    }
    const overflow = content.scrollWidth - availableWidth;
    const scrollable = overflow > 1;
    setIsScrollable(scrollable);
    if (scrollable) {
      container.style.setProperty('--overflow', `${overflow}px`);
    } else {
      container.style.removeProperty('--overflow');
    }
  }, []);
  useEffect(() => {
    updateOverflow();
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;
    const observer = new ResizeObserver(updateOverflow);
    observer.observe(container);
    observer.observe(content);
    if (container.parentElement) {
      observer.observe(container.parentElement);
    }
    return () => observer.disconnect();
  }, [updateOverflow, children]);
  const showTooltip = () => {
    if (!isScrollable || !title || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setTooltipPos({
      top: rect.bottom,
      left: rect.left + rect.width / 2
    });
  };
  const hideTooltip = () => setTooltipPos(null);
  return <>
      <div ref={containerRef} className={`${styles.container} ${isScrollable ? styles.scrollable : ''} ${className ?? ''}`} role={isScrollable ? 'region' : undefined} aria-label={isScrollable ? title : undefined} onMouseEnter={showTooltip} onMouseLeave={hideTooltip}>
        <span ref={contentRef} className={styles.content}>
          {children}
        </span>
      </div>
      {tooltipPos && title && createPortal(<span className={styles.tooltip} style={{
      top: tooltipPos.top,
      left: tooltipPos.left
    }}>
          {title}
        </span>, document.body)}
    </>;
};
export default ScrollablePriceText;